import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Loader2, X, ImageIcon } from "lucide-react";
import { api, BACKEND_URL, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function PhotoGallery({ serviceId }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [pendingKind, setPendingKind] = useState("before");
  const fileRef = useRef(null);
  const token = localStorage.getItem("servisku_token");

  const load = async () => {
    try {
      const { data } = await api.get(`/services/${serviceId}/photos`);
      setPhotos(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [serviceId]);

  const chooseFile = (kind) => {
    setPendingKind(kind);
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran maksimal 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/services/${serviceId}/photos?kind=${pendingKind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto berhasil diunggah");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal mengunggah";
      toast.error(typeof d === "string" ? d : "Gagal mengunggah");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await api.delete(`/photos/${p.id}`);
      toast.success("Foto dihapus");
      setPhotos((list) => list.filter((x) => x.id !== p.id));
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal menghapus";
      toast.error(typeof d === "string" ? d : "Gagal menghapus");
    }
  };

  const photoUrl = (id) => `${BACKEND_URL}/api/photos/${id}?auth=${encodeURIComponent(token || "")}`;

  const groups = {
    before: photos.filter((p) => p.kind === "before"),
    after: photos.filter((p) => p.kind === "after"),
  };

  const canDelete = (p) => user?.role === "admin" || p.uploaded_by === user?.id;

  const Section = ({ title, kind, list, icon: Icon }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
          <Icon className="w-4 h-4" /> {title} ({list.length})
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => chooseFile(kind)}
          disabled={uploading}
          data-testid={`upload-${kind}-btn`}
          className="gap-1 h-8"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Tambah
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center text-xs text-slate-500">
          Belum ada foto {title.toLowerCase()}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {list.map((p) => (
            <div
              key={p.id}
              className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200"
              data-testid={`photo-thumb-${p.id}`}
            >
              <img
                src={photoUrl(p.id)}
                alt=""
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setLightbox(p)}
                loading="lazy"
              />
              {canDelete(p) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(p);
                  }}
                  data-testid={`photo-delete-${p.id}`}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-5" data-testid="photo-gallery">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
          <Camera className="w-4 h-4 text-blue-600" /> Foto Barang
        </h3>
        <span className="text-[10px] text-slate-400">JPG · PNG · WebP · Maks 5MB</span>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="photo-file-input"
      />

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-5">
          <Section title="Sebelum" kind="before" list={groups.before} icon={ImageIcon} />
          <Section title="Sesudah" kind="after" list={groups.after} icon={ImageIcon} />
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-sm">
              Foto {lightbox?.kind === "after" ? "Sesudah" : "Sebelum"} —{" "}
              <span className="text-slate-500 font-normal">{formatDate(lightbox?.created_at)}</span>
            </DialogTitle>
          </DialogHeader>
          {lightbox && (
            <div className="bg-slate-900 flex items-center justify-center max-h-[80vh]">
              <img
                src={photoUrl(lightbox.id)}
                alt=""
                className="max-w-full max-h-[80vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
